import React, { forwardRef, useEffect, useRef, useState } from 'react'
import styles from './ChatMessage.module.scss'
import { ReactComponent as RobotIcon } from 'components/CRM/UI/icons/robot.svg'
import { ReactComponent as PhoneIcon } from 'components/CRM/UI/icons/Calling.svg'
import { ReactComponent as PinIcon } from 'components/CRM/UI/icons/pushpin-line.svg'
import { ReactComponent as ShowScript } from 'components/UI/icons/file-list-2-line.svg'
import { ReactComponent as WhatsAppIcon } from 'components/CRM/UI/icons/whatsapp.svg'
import { ReactComponent as JpegIcon } from 'components/CRM/UI/icons/jpeg.svg'
import { ReactComponent as PngIcon } from 'components/CRM/UI/icons/png.svg'
import { ReactComponent as JpgIcon } from 'components/CRM/UI/icons/jpg.svg'
import { ReactComponent as TiffIcon } from 'components/CRM/UI/icons/tiff.svg'
import { ReactComponent as WordIcon } from 'components/CRM/UI/icons/word.svg'
import { ReactComponent as Mp3Icon } from 'components/CRM/UI/icons/mp3.svg'
import { ReactComponent as PdfIcon } from 'components/CRM/UI/icons/pdf.svg'
import { ReactComponent as XlsIcon } from 'components/CRM/UI/icons/xls.svg'
import { ReactComponent as XlsxIcon } from 'components/CRM/UI/icons/xlsx.svg'
import { ReactComponent as PptIcon } from 'components/CRM/UI/icons/ppt.svg'
import { ReactComponent as PptxIcon } from 'components/CRM/UI/icons/pptx.svg'
import { ReactComponent as TxtIcon } from 'components/CRM/UI/icons/txt.svg'
import { ReactComponent as ZipIcon } from 'components/CRM/UI/icons/zip.svg'
import { ReactComponent as RarIcon } from 'components/CRM/UI/icons/rar.svg'
import { ReactComponent as Mp4Icon } from 'components/CRM/UI/icons/mp4.svg'
import { ReactComponent as EditIcon } from 'components/UI/icons/edit-line.svg'
import { ReactComponent as DeleteIcon } from 'components/UI/icons/delete-bin-6-line.svg'
import ProfileAvatar from 'components/CRM/UI/components/ProfileAvatar/ProfileAvatar'
import useWindowWidth from 'components/CRM/utils/hooks/useWindowWidth'
import { superAxiosRequest } from 'axiosApi'
import Stores from 'Stores'
import useLocalization from 'hooks/useLocalization'
import swal from 'sweetalert'
import asideModalStore from 'Stores/asideModalStore'
import EditMessage from '../../../../ContentBox/Global/GlobalModalSingle/templates/CRM/EditMessage/EditMessage'

const formatToIcon = {
  png: <PngIcon />,
  jpeg: <JpegIcon />,
  jpg: <JpgIcon />,
  tif: <TiffIcon />,
  doc: <WordIcon />,
  docx: <WordIcon />,
  xls: <XlsIcon />,
  xlsx: <XlsxIcon />,
  pptx: <PptxIcon />,
  ppt: <PptIcon />,
  txt: <TxtIcon />,
  zip: <ZipIcon />,
  rar: <RarIcon />,
  mp3: <Mp3Icon />,
  mpeg: <Mp3Icon />,
  mp4: <Mp4Icon />,
  pdf: <PdfIcon />,
}

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const highlightText = (text, searchTerm) => {
  if (!searchTerm) return text
  const escapedSearchTerm = escapeRegExp(searchTerm)
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
  const parts = text.split(regex)
  return parts.map((part, index) =>
    part?.toLowerCase() === searchTerm?.toLowerCase() ? (
      <mark key={index}>{part}</mark>
    ) : (
      part
    ),
  )
}

const ChatMessage = forwardRef(
  (
    {
      message,
      dealData,
      messageStatus,
      isHighlighted,
      setMessages,
      type,
      error,
      fetchMessages,
      pinPage,
      setShouldScroll,
      searchTerm,
      newMessageId,
      editTask,
      dealId,
      sessionId,
      channelId,
    },
    ref,
  ) => {
    const [showTranscription, setShowTranscription] = useState(false)
    const [currentDate, setCurrentDate] = useState(null)
    const [pin, sePin] = useState(message?.is_pinned)
    const [showActions, setShowActions] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const [canEdit, setCanEdit] = useState(false)
    const [canDelete, setCanDelete] = useState(false)

    const { translate } = useLocalization({
      section: 'crm',
      subsection: 'customer_card',
      dict: {
        task_type: 'Task type',
        start_finish_time: 'Start & Finish Time',
        start_finish_date: 'Start & Finish Date',
        incoming_call: 'Incoming call',
        outgoing_call: 'Outgoing call',
        call_from: 'from',
        call_to: 'to',
        no_answer: 'No answer from subscriber',
        conversation_not_held: "The conversation didn't take place",
        today: 'Today',
        yesterday: 'Yesterday',
        conversation_took_place: 'The conversation took place',
      },
    })

    const windowWidth = useWindowWidth()
    const width = windowWidth <= 1024 ? '16px' : '40px'
    const fs = windowWidth <= 1024 ? '7px' : '16px'

    const checkTimePermissions = () => {
      if (!message?.sent_at) return { canEdit: false, canDelete: false }

      const sentDate = new Date(message.sent_at)
      const now = new Date()
      const diffInMinutes = (now - sentDate) / (1000 * 60)
      const diffInHours = diffInMinutes / 60

      const canEdit = message?.type_message === 'text' && diffInMinutes <= 15
      const canDelete = diffInHours <= 24

      return { canEdit, canDelete }
    }

    useEffect(() => {
      const { canEdit, canDelete } = checkTimePermissions()
      setCanEdit(canEdit)
      setCanDelete(canDelete)
    }, [message?.sent_at, message?.type_message])

    // useEffect(() => {
    //   const interval = setInterval(() => {
    //     const { canEdit, canDelete } = checkTimePermissions()
    //     setCanEdit(canEdit)
    //     setCanDelete(canDelete)
    //   }, 60000)
    //
    //   return () => clearInterval(interval)
    // }, [message?.sent_at, message?.type_message])

    const editMessage = () => {
      if (!canEdit) {
        swal(
          'Ошибка',
          'Редактирование сообщения доступно только в течение 15 минут после отправки',
          'error',
        )
        return
      }

      const ms = {
        modalBodyComponent: () => <EditMessage />,
        button: 'Сохранить',
        link: `integration/messages/${message?.id}`,
        title: 'Редактировать сообщение',
        crm: true,
        is_put: true,
        isResponsive: true,
        onSubmitSuccess: (res) => {},
        valid: {
          text: 'required',
        },
      }

      const md = {
        text: message?.text?.trim(),
      }

      asideModalStore.activity(ms, md)
    }

    const deleteMessage = () => {
      if (!canDelete) {
        swal(
          'Ошибка',
          'Удаление сообщения доступно только в течение 24 часов после отправки',
          'error',
        )
        return
      }

      swal({
        title: 'Вы уверены?',
        text: 'Это действие нельзя отменить',
        icon: 'warning',
        buttons: ['Отмена', 'Удалить'],
        dangerMode: true,
      }).then((yes) => {
        if (yes) {
          superAxiosRequest(
            {
              link: `integration/messages/${message?.id}`,
              method: 'delete',
              crm: true,
            },
            {},
          ).catch((error) => {
            swal('Ошибка', 'Не удалось удалить сообщение', 'error')
          })
        }
      })
    }

    useEffect(() => {
      let date = null

      if (type === 'message' && message.sent_at) {
        date = new Date(message.sent_at)
      } else if (type === 'task' && message.created_at) {
        date = new Date(message.created_at)
      } else if (type === 'call' && message.started_at) {
        date = new Date(message.started_at)
      }

      setCurrentDate(date)
    }, [message?.sent_at, message?.created_at, message?.started_at])

    // useEffect(() => {
    //   setMessages((prevMessages) =>
    //     prevMessages.map((message) =>
    //       message.data.id === newMessageId
    //         ? {
    //             ...message,
    //             data: {
    //               ...message.data,
    //               sending_status: messageStatus,
    //             },
    //           }
    //         : message,
    //     ),
    //   )
    // }, [messageStatus, newMessageId])

    const returnCurrentData = () => {
      if (!currentDate) {
        return ''
      }

      const today = new Date()
      const yesterday = new Date(today.getTime() - 86400000)

      today.setHours(0, 0, 0, 0)
      yesterday.setHours(0, 0, 0, 0)

      if (currentDate >= today) {
        return `${translate.today} ${currentDate.toLocaleTimeString()}`
      } else if (currentDate >= yesterday) {
        return `${translate.yesterday} ${currentDate.toLocaleTimeString()}`
      } else {
        return `${currentDate.toLocaleDateString()} ${currentDate.toLocaleTimeString()}`
      }
    }

    function calculateCallDuration(started_at, finishedAt) {
      const start = new Date(started_at)
      const end = new Date(finishedAt)
      const duration = end.getTime() - start.getTime()

      const hours = Math.floor(duration / (1000 * 60 * 60))
      const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((duration % (1000 * 60)) / 1000)

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const parseFileName = (filePath) => {
      if (!filePath) return { name: '', format: '' }
      const fileName = filePath
        .toLowerCase()
        .substring(filePath.lastIndexOf('/') + 1)
      const fileFormat = fileName
        .toLowerCase()
        .substring(fileName.lastIndexOf('.') + 1)

      return { name: fileName, format: fileFormat }
    }

    const renderMediaContent = (message) => {
      const filePath = message?.file?.file
      const { name, format } = parseFileName(filePath)
      const bytesToMegabytes = message?.file?.file_size / (1024 * 1024)
      const megabytes =
        bytesToMegabytes < 0.01
          ? bytesToMegabytes.toFixed(3)
          : bytesToMegabytes < 0.1
            ? bytesToMegabytes.toFixed(2)
            : bytesToMegabytes.toFixed(1)

      const renderIcon = (format) => formatToIcon[format] || null

      return (
        <>
          <a
            className={styles.fileIconContainer}
            href={Stores.baseLinkCRM() + message?.file?.file}
            target='_blank'
            download
          >
            {message?.type_message === 'image' ? (
              <img
                className={styles.typeImage}
                src={Stores.baseLinkCRM() + message?.file?.file}
                alt='image'
              />
            ) : (
              <>
                {renderIcon(format)}
                {megabytes} MB
              </>
            )}
          </a>
          <div className={styles.message}>{name}</div>
          {message?.text && <p className={styles.message}>{message?.text}</p>}
          <div className={styles.date}>
            {returnCurrentData()}{' '}
            {message?.channel?.name && `| ${message?.channel?.name}`}
          </div>
        </>
      )
    }

    const pinMessage = (messageId, isTask = false) => {
      setShouldScroll(false)
      superAxiosRequest(
        {
          link: `integration/messages/pin`,
          method: 'post',
          crm: true,
        },
        {
          ...(isTask
            ? { task_ids: [messageId] }
            : { message_ids: [messageId] }),
        },
      )
        .then((res) => {
          const updatedMessageRes = res?.data[0]?.data
          if (pinPage) {
            const updatedMessage = res?.data[0]
            setMessages((prevMessages) => {
              return prevMessages.map((message) =>
                message?.data?.id === messageId ? updatedMessage : message,
              )
            })
          } else {
            const updatedMessage = res?.data[0]
            setMessages((prevMessages) => {
              return prevMessages.map((message) =>
                message?.data?.id === messageId ? updatedMessage : message,
              )
            })
            sePin((prevPin) => updatedMessageRes.is_pinned)
          }
        })
        .catch((error) => {
          console.error(error)
        })
    }

    const renderMessages = (type) => {
      switch (type) {
        case 'message':
          return message?.sender_client ? (
            <>
              <div className={styles.clientAvatar}>
                <ProfileAvatar
                  fs={fs}
                  width={width}
                  height={width}
                  name={dealData?.client?.name || dealData?.lead?.client?.name}
                />
                <div className={styles.cycleForIcon}>
                  <div className={styles.whatsAppIcon}>
                    <WhatsAppIcon />
                  </div>
                </div>
              </div>
              <div className={`${styles.messageBlock} ${styles.clientBlock}`}>
                {message?.type_message === 'text' && (
                  <>
                    <p className={styles.message}>
                      {highlightText(message?.text, searchTerm)}
                    </p>
                    <div className={styles.date}>
                      {returnCurrentData()}{' '}
                      {message?.channel?.name && `| ${message?.channel?.name}`}
                    </div>
                  </>
                )}
                {(message?.type_message === 'image' ||
                  message?.type_message === 'document' ||
                  message?.type_message === 'audio' ||
                  message?.type_message === 'video') &&
                  renderMediaContent(message)}
              </div>
              <PinIcon
                className={`${styles.pin} ${pin ? styles.pinMessage : ''}`}
                onClick={() => pinMessage(message?.id)}
              />
            </>
          ) : (
            <div
              className={styles.messageWrapper}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className={styles.messageActions}>
                {(isHovered || showActions) &&
                  !message?.is_active === false &&
                  message?.sending_status === 2 && (
                    <>
                      {canDelete && (
                        <DeleteIcon
                          onClick={() => deleteMessage()}
                          className={styles.pin}
                        />
                      )}
                      {canEdit && (
                        <EditIcon
                          onClick={() => editMessage()}
                          className={styles.pin}
                        />
                      )}
                    </>
                  )}
                <PinIcon
                  className={`${styles.pin} ${pin ? styles.pinMessage : ''}`}
                  onClick={() => pinMessage(message?.id)}
                />{' '}
              </div>
              <div
                className={`
                ${styles.messageBlock} 
                ${styles.messageBlock} 
                ${message.sending_status === 1 ? styles.sending : styles.success} 
                `}
              >
                {message?.type_message === 'text' && (
                  <>
                    <p className={styles.message}>
                      {highlightText(message?.text, searchTerm)}
                    </p>
                    <div className={styles.date}>{returnCurrentData()}</div>
                    {message?.created_by_employee && (
                      <div className={styles.name}>
                        <div> {message?.created_by_employee.name} </div>
                        <div>
                          {message?.employee_name} {message?.channel?.name}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {(message?.type_message === 'image' ||
                  message?.type_message === 'document' ||
                  message?.type_message === 'audio' ||
                  message?.type_message === 'video') &&
                  renderMediaContent(message)}
                {error && message?.error_description === '7_DAYS_EXCEEDED' ? (
                  <div className={styles.errorNote}>
                    7-дневное диалоговое окно закрыто. Написать в директ можно
                    только в течение 7 дней с последнего входящего сообщения.
                    Ответить на комментарий в директ можно только в течение семи
                    дней с момента публикации комментария.
                  </div>
                ) : error ? (
                  <div className={styles.errorNote}>
                    The message wasn't sent. Try again.
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
              <RobotIcon />
            </div>
          )
        case 'call':
          const transcriptionLines =
            message?.voice_record?.transcription &&
            message?.voice_record?.transcription.split(/\r?\n/)
          const textForCurrentCallType =
            message.status === 'BUSY'
              ? 'Subscriber is busy'
              : message.status === 'NOANSWER'
                ? translate.no_answer
                : message.status === 'CANCEL'
                  ? 'Call canceled'
                  : message.status === 'CONGESTION'
                    ? 'Network congestion'
                    : 'Subscriber unavailable'

          return message.status === 'ANSWER' ? (
            <div className={styles.callContainer}>
              <PhoneIcon />
              <div className={`${styles.callBlock}`}>
                <p className={styles.callHeader}>
                  {`${returnCurrentData()} 
                                ${message.is_outgoing ? `${translate.outgoing_call} ${translate.call_from}` : `${translate.incoming_call} ${translate.call_from}`}: ${message.from_number} 
                                   ${translate.call_to} ${message.to_number}`}
                </p>
                <p className={styles.callDuration}>
                  {message.is_outgoing
                    ? `${translate.outgoing_call}`
                    : `${translate.incoming_call}`}{' '}
                  {calculateCallDuration(
                    message.started_at,
                    message.finished_at,
                  )}
                </p>
                <p className={styles.callScript}>
                  {translate.conversation_took_place}
                </p>
                {message?.voice_record?.file && (
                  <div className={styles.audioContainer}>
                    <audio
                      src={`${Stores.baseLinkCRM()}${message?.voice_record?.file}`}
                      controls
                      preload='auto'
                    />
                    {message?.voice_record?.transcription && (
                      <div
                        className={styles.scriptIcon}
                        title={'Transcription'}
                        onClick={() => setShowTranscription(!showTranscription)}
                      >
                        <ShowScript />
                      </div>
                    )}
                  </div>
                )}
                {showTranscription && (
                  <div
                    className={`${styles.callScript} ${styles.transcription} ${showTranscription ? styles.show : ''}`}
                  >
                    {transcriptionLines.map((line, index) => (
                      <div key={index}>-{line}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.callContainer}>
              <PhoneIcon />
              <div className={`${styles.callBlock}`}>
                <p className={styles.callHeader}>
                  {`${returnCurrentData()} 
                                        ${translate.incoming_call} ${translate.call_from} ${message.from_number} 
                                        ${translate.call_to} ${message.to_number}`}
                </p>
                <p className={styles.callDuration}>{textForCurrentCallType}</p>
                <p className={styles.callScript}>
                  {translate.conversation_not_held}
                </p>
              </div>
            </div>
          )

        case 'task':
          const startTask = message?.start_task
          const finishTask = message?.finish_task

          const addLeadingZero = (number) =>
            number < 10 ? `0${number}` : number

          const formatDate = (dateString) => {
            const date = new Date(dateString)
            return `${addLeadingZero(date.getDate())}.${addLeadingZero(date.getMonth() + 1)}.${date.getFullYear()}`
          }

          const formatTime = (dateString) => {
            const date = new Date(dateString)
            return `${addLeadingZero(date.getHours())}:${addLeadingZero(date.getMinutes())}`
          }

          const startDate = startTask && formatDate(startTask)
          const finishDate = finishTask && formatDate(finishTask)
          const startTime = startTask && formatTime(startTask)
          const finishTime = finishTask && formatTime(finishTask)

          const getTaskBorderStyle = (finishTask, message) => {
            let styles = {}

            // Определяем стили границы на основе даты
            if (finishTask) {
              const now = new Date()
              const finishDate = new Date(finishTask)

              if (finishDate < now) {
                styles.border = '2px solid #ff4444'
              } else {
                const diffInHours = (finishDate - now) / (1000 * 60 * 60)

                if (diffInHours <= 24) {
                  styles.border = '2px solid #00C851'
                } else {
                  styles.border = '2px solid #cccccc'
                }
              }
            }

            // Добавляем стили для статусов 3 или 4 (отклонена/завершена)
            if (message?.status?.id === 3 || message?.status?.id === 4) {
              styles = {
                ...styles,
                textDecoration: 'line-through',
              }
            }

            return styles
          }

          return (
            <div className={styles.taskContainer}>
              <img src={message?.task_category?.text_file} alt='icon' />
              <div
                className={`${styles.taskBlock}`}
                style={getTaskBorderStyle(finishTask, message)}
              >
                <div>
                  <div
                    className={styles.taskCreate}
                  >{`${returnCurrentData()}`}</div>
                  <div className={styles.taskTitle}>
                    {new Date(message?.finish_task).toLocaleDateString()} for{' '}
                    {message?.assignee?.full_name}
                  </div>
                  <div className={styles.taskInfo}>
                    <span>{translate.task_type}: </span>
                    {message.task_category?.name}
                  </div>
                  <div className={styles.taskInfo}>
                    <span>{translate.start_finish_time}: </span>
                    {finishTask
                      ? `${startTime} - ${finishTime}`
                      : `${startTime} - `}
                  </div>
                  <div className={styles.taskInfo}>
                    <span>{translate.start_finish_date}: </span>
                    {finishTask
                      ? `${startDate} - ${finishDate}`
                      : `${startDate} - `}
                  </div>
                  {message?.comment && (
                    <div className={styles.taskInfo}>
                      <span>Comment: </span> {message.comment}
                    </div>
                  )}
                </div>

                <EditIcon
                  onClick={() => editTask(message)}
                  className={styles.editDateBlock}
                />
              </div>
              <div>
                <PinIcon
                  className={`${styles.pin} ${pin ? styles.pinMessage : ''}`}
                  onClick={() => pinMessage(message?.id, true)}
                />
              </div>
            </div>
          )
        default:
          break
      }
    }

    return (
      <div
        ref={ref}
        className={`${styles.messageContainer} ${message?.sender_client || type === 'call' ? styles.client : styles.manager}`}
      >
        {renderMessages(type)}
      </div>
    )
  },
)

export default ChatMessage
